import { useState } from 'react'
import { ToolCallBadge } from './tool-call-badge'
import { CompactionCard } from './compaction-card'
import { AssistantMarkdown } from '../../../assistant-markdown'
import { UserToolResult } from './user-tool-result'
import { formatCost, formatTokens, formatDurationMs } from '@lens/lib/decode'
import type { ReplayTurn, CompactionEvent } from '@lens/types/claude'
import { Badge } from "@excellent/ui"
import { Button } from "@excellent/ui"
import { cn } from "@excellent/ui"
import { formatDateTime } from "../../../../format"
import { shortModelName } from "../../chart-config"
import { ChevronDown, ChevronUp, Brain, Clock, Coins } from "../../../../icons"

/** Show “Show more” when assistant text exceeds this length (markdown; avoid slicing mid-block). */
const ASSISTANT_COLLAPSE_THRESHOLD = 900

interface TurnCardProps {
  turn: ReplayTurn
  turnNumber: number
  compactionBefore?: CompactionEvent
  toolResults: Map<string, { content: string; is_error: boolean }>
}

function TokenBreakdown({ turn }: { turn: ReplayTurn }) {
  if (!turn.usage) return null
  const u = turn.usage
  const items = [
    u.input_tokens       ? { label: 'In',     value: u.input_tokens,       color: 'var(--viz-sky)' } : null,
    u.output_tokens      ? { label: 'Out',    value: u.output_tokens,      color: 'var(--chart-4)' } : null,
    u.cache_creation_input_tokens ? { label: 'cW', value: u.cache_creation_input_tokens, color: 'var(--chart-5)' } : null,
    u.cache_read_input_tokens     ? { label: 'cR', value: u.cache_read_input_tokens,     color: 'var(--chart-1)' } : null,
  ].filter(Boolean) as { label: string; value: number; color: string }[]

  if (items.length === 0) return null

  return (
    <div className="flex items-center gap-1 flex-wrap">
      <Coins className="w-3 h-3 text-muted-foreground shrink-0" />
      {items.map(({ label, value, color }) => (
        <span
          key={label}
          className="type-body font-mono px-1.5 py-0.5 rounded surface-edge bg-muted/50"
          style={{ color }}
        >
          {label}:{formatTokens(value)}
        </span>
      ))}
      {turn.estimated_cost ? (
        <span className="type-body font-mono text-[var(--chart-4)] px-1 py-0.5">
          {formatCost(turn.estimated_cost)}
        </span>
      ) : null}
    </div>
  )
}

export function UserTurnCard({ turn, compactionBefore, toolResults }: TurnCardProps) {
  return (
    <div>
      {compactionBefore && <CompactionCard event={compactionBefore} />}

      <div className="mb-5 flex flex-col items-end gap-1.5">
        {/* Timestamp label */}
        <span className="type-body text-muted-foreground pr-1">
          {formatDateTime(turn.timestamp)}
        </span>

        {/* User bubble (right-aligned) */}
        {turn.text && (
          <div className="max-w-[85%] bg-primary/10 border border-primary/20 rounded-lg rounded-tr-sm px-4 py-3">
            <p className="type-body text-foreground whitespace-pre-wrap leading-body">
              {turn.text}
            </p>
          </div>
        )}

        {/* Tool results (user feedback) */}
        {turn.tool_results && turn.tool_results.length > 0 && (
          <div className="flex w-full max-w-[90%] flex-col gap-2">
            {turn.tool_results.map(r => (
              <UserToolResult key={r.tool_use_id} content={r.content} isError={r.is_error} />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export function AssistantTurnCard({ turn, turnNumber, toolResults }: TurnCardProps) {
  const [thinkingOpen, setThinkingOpen] = useState(false)
  const [expanded, setExpanded] = useState(false)

  const modelShort = turn.model ? shortModelName(turn.model) : 'Claude'

  const textToShow = turn.text ?? ''
  const needsExpandToggle = textToShow.length > ASSISTANT_COLLAPSE_THRESHOLD

  return (
    <div className="mb-6 flex flex-col gap-1.5">
      {/* Header row */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-1.5">
          <div className="w-6 h-6 rounded-lg bg-primary/20 border border-primary/30 flex items-center justify-center shrink-0">
            <span className="type-body  text-foreground">C</span>
          </div>
          <span className="type-body  text-foreground">Claude</span>
        </div>
        <Badge variant="outline" className="type-body px-1.5 py-0 h-5 font-mono">
          {modelShort}
        </Badge>
        <span className="type-body text-muted-foreground">#{turnNumber}</span>
        {turn.turn_duration_ms && (
          <span className="type-body text-muted-foreground flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {formatDurationMs(turn.turn_duration_ms)}
          </span>
        )}
      </div>

      {/* Thinking block */}
      {turn.has_thinking && (
        <div className="ml-8">
          {/* button-ignore: disclosure trigger — auto-height multi-line
              expand/collapse toggle, not a standalone action button. */}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-auto gap-1.5 px-2 py-1.5 type-body  text-muted-foreground hover:bg-muted hover:text-foreground"
            onClick={() => setThinkingOpen(o => !o)}
          >
            <Brain className="h-3.5 w-3.5 shrink-0" />
            Extended thinking
            <ChevronDown
              className={cn('h-3.5 w-3.5 shrink-0 transition-transform duration-200', thinkingOpen && 'rotate-180')}
            />
          </Button>
          {thinkingOpen && turn.thinking_text && (
            <div className="mt-1 bg-muted surface-edge rounded-lg px-4 py-3  ">
              <pre className="type-body text-foreground whitespace-pre-wrap max-h-56 overflow-auto leading-body /50">
                {turn.thinking_text.slice(0, 3000)}
                {turn.thinking_text.length > 3000 && (
                  <span className="text-muted-foreground"> …[{(turn.thinking_text.length - 3000).toLocaleString()} more chars]</span>
                )}
              </pre>
            </div>
          )}
        </div>
      )}

      {/* Tool calls */}
      {turn.tool_calls && turn.tool_calls.length > 0 && (
        <div className="ml-8 space-y-1">
          {turn.tool_calls.map(tc => (
            <ToolCallBadge
              key={tc.id}
              tool={tc}
              result={toolResults.get(tc.id)}
            />
          ))}
        </div>
      )}

      {/* Response text bubble */}
      {textToShow && (
        <div className="ml-8">
          <div className="rounded-lg rounded-tl-sm surface-edge bg-card px-4 py-3">
            <div
              className={cn(
                'relative',
                needsExpandToggle && !expanded && 'max-h-112 overflow-hidden'
              )}
            >
              <AssistantMarkdown content={textToShow} />
              {needsExpandToggle && !expanded && (
                <div
                  className="pointer-events-none absolute inset-x-0 bottom-0 h-14 bg-linear-to-t from-card to-transparent"
                  aria-hidden
                />
              )}
            </div>
            {needsExpandToggle && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="mt-2 gap-1 text-muted-foreground hover:text-foreground"
                onClick={() => setExpanded(e => !e)}
              >
                {expanded ? (
                  <>
                    <ChevronUp className="h-3 w-3" /> Show less
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-3 w-3" /> Show full response
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Token breakdown */}
      {turn.usage && (
        <div className="ml-8 mt-0.5">
          <TokenBreakdown turn={turn} />
        </div>
      )}
    </div>
  )
}
